import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration } from '../../../data/configuration.js';
import { CameraController, CameraViews } from '../../../camera-controller.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import { getFilePathForGlossPaintColourId, getFilePathForMattePaintColourId } from '../../paintwork-view.tsx';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import ViewContentRows from '../../ui/view-content-rows.tsx';
import { Utilities } from '../../../utilities/utilities.ts';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface DashboardViewProps {
	interiorConfigurator: InteriorConfigurator;
	cameraController: CameraController;
	onBackButtonClicked: () => void;
}

interface DashboardViewContextType {
	selectedDashboardItemId: string;
	handleDashboardItemClicked: (id: string) => void;
	selectedSteeringWheelItemId: string;
	handleSteeringWheelItemClicked: (id: string) => void;
	selectedSteeringColumnItemId: string;
	handleSteeringColumnItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function DashboardView(props: DashboardViewProps) {

	// initial values.
	const initialDashboardItemId = Configuration.dashboardOption;
	const initialDashboardItemName = () => getItemName(initialDashboardItemId);
	const initialSteeringWheelItemId = Configuration.steeringWheelOption;
	const initialSteeringWheelItemName = () => getSteeringWheelItemName(initialSteeringWheelItemId);
	const initialSteeringColumnItemId = Configuration.steeringColumnOption;
	const initialSteeringColumnItemName = () => getSteeringWheelItemName(initialSteeringColumnItemId);
	
	// states.
	const [selectedDashboardItemId, setSelectedDashboardItemId] = useState(initialDashboardItemId);
	const [selectedDashboardItemName, setSelectedDashboardItemName] = useState(initialDashboardItemName);
	const [selectedSteeringWheelItemId, setSelectedSteeringWheelItemId] = useState(initialSteeringWheelItemId);
	const [selectedSteeringWheelItemName, setSelectedSteeringWheelItemName] = useState(initialSteeringWheelItemName);
	const [selectedSteeringColumnItemId, setSelectedSteeringColumnItemId] = useState(initialSteeringColumnItemId);
	const [selectedSteeringColumnItemName, setSelectedSteeringColumnItemName] = useState(initialSteeringColumnItemName);
	
	// handlers.

	const handleDashboardItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedDashboardItemId(id);
		setSelectedDashboardItemName(getItemName(id));

		// update the configuration.
		Configuration.dashboardOption = id;
		Configuration.updateCosts();

		// select the dashboard option on the car.
		props.interiorConfigurator.selectDashboardOption(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.DashboardView);
	};

	const handleSteeringWheelItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedSteeringWheelItemId(id);
		setSelectedSteeringWheelItemName(getSteeringWheelItemName(id));

		// update the configuration.
		Configuration.steeringWheelOption = id;
		Configuration.updateCosts();

		// select the steering wheel option on the car.
		props.interiorConfigurator.selectSteeringWheelOption(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.SteeringWheelView);
	};

	const handleSteeringColumnItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedSteeringColumnItemId(id);
		setSelectedSteeringColumnItemName(getSteeringWheelItemName(id));

		// update the configuration.
		Configuration.steeringColumnOption = id;
		Configuration.updateCosts();

		// select the steering column option on the car.
		props.interiorConfigurator.selectSteeringColumnOption(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.SteeringWheelView);
	};

	// return the HTML.
	return (
		<DashboardViewContext.Provider value={{ selectedDashboardItemId, handleDashboardItemClicked, selectedSteeringWheelItemId, handleSteeringWheelItemClicked, selectedSteeringColumnItemId, handleSteeringColumnItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-dashboard">DASHBOARD</h2>
					</div>

					<SectionPanel
						subHeading={'FASCIA'}
						modalContent={CloudData.getModalContent(selectedDashboardItemId, CloudData.dashboardOptions)}
					>
						<SelectedSectionItem
							itemName={selectedDashboardItemName}
							sectionPrice={CloudData.getPrice(selectedDashboardItemId, CloudData.dashboardOptions, { ref: false })}
						/>
						<DashboardItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'STEERING WHEEL'}
						modalContent={CloudData.getModalContent(selectedSteeringWheelItemId, CloudData.steeringWheelOptions)}
					>
						<SelectedSectionItem
							itemName={selectedSteeringWheelItemName}
							sectionPrice={CloudData.getPrice(selectedSteeringWheelItemId, CloudData.steeringWheelOptions, { ref: false })}
						/>
						<SteeringWheelItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'COLUMN SHROUD'}
						modalContent={CloudData.getModalContent(selectedSteeringColumnItemId, CloudData.steeringWheelOptions)}
					>
						<SelectedSectionItem
							itemName={selectedSteeringColumnItemName}
							sectionPrice={CloudData.getPrice(selectedSteeringColumnItemId, CloudData.steeringWheelOptions, { ref: false })}
						/>
						<SteeringColumnItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</DashboardViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const DashboardViewContext = createContext<DashboardViewContextType | undefined>(undefined);

function DashboardItems() {
	return (
		<ViewContentRows>
			<DashboardItem id={'D024'} imageFilePath={'matte-silver'} />
			<DashboardItem id={'D026'} imageFilePath={'gloss-black'} />
			<DashboardItem id={'D001'} imageFilePath={'body-coloured-dynamically-generated'} />
			<DashboardItem id={'D027'} imageFilePath={'matte-body-coloured-dynamically-generated'} />
		</ViewContentRows>
	);
}

function DashboardItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(DashboardViewContext);
	if (!context) {
		throw new Error('DashboardViewContext must be used within a DashboardView component');
	}

	// if the 'gloss body coloured' or 'matte body coloured' options are chosen, show the current body paint instead of the given image.
	let imageFilePath = `assets/images/configuration-items/interior/dashboard/${props.imageFilePath}.webp`;
	if (props.id === Configuration.dashboardMatchingGlossBodyColourId) {
		imageFilePath = getBodyColourImageFilePath(getFilePathForGlossPaintColourId);
	}
	else if (props.id === Configuration.dashboardMatchingMatteBodyColourId) {
		imageFilePath = getBodyColourImageFilePath(getFilePathForMattePaintColourId);
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={imageFilePath}
			isSelected={props.id === context.selectedDashboardItemId}
			onItemClicked={context.handleDashboardItemClicked}
		/>
	);
}

function SteeringWheelItems() {
	return Configuration.isSupersport400Model() ? <SteeringWheelItems400 /> : <SteeringWheelItemsStandard />;
}

function SteeringWheelItemsStandard() {
	return (
		<ViewContentRows>
			<SteeringWheelItem id={'SWC001'} imageFilePath={'silver'} />
			<SteeringWheelItem id={'SWC002'} imageFilePath={'black'} />
		</ViewContentRows>
	);
}

function SteeringWheelItems400() {
	return (
		<ViewContentRows>
			<SteeringWheelItem id={'SWC001'} imageFilePath={'black-leather'} />
			<SteeringWheelItem id={'SW010'} imageFilePath={'black-alcantara'} />
		</ViewContentRows>
	);
}

function SteeringWheelItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(DashboardViewContext);
	if (!context) {
		throw new Error('DashboardViewContext must be used within a DashboardView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getSteeringWheelItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/steering-wheel/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedSteeringWheelItemId}
			onItemClicked={context.handleSteeringWheelItemClicked}
		/>
	);
}

function SteeringColumnItems() {
	return (
		<ViewContentRows>
			<SteeringColumnItem id={'SWC006'} imageFilePath={'black-composite'} />
			<SteeringColumnItem id={'SWC007'} imageFilePath={'black-leather'} />
		</ViewContentRows>
	);
}

function SteeringColumnItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(DashboardViewContext);
	if (!context) {
		throw new Error('DashboardViewContext must be used within a DashboardView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getSteeringWheelItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/steering-column/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedSteeringColumnItemId}
			onItemClicked={context.handleSteeringColumnItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.dashboardOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getSteeringWheelItemName(id: string): string {
	return CloudData.getName(id, CloudData.steeringWheelOptions);
}

// returns the image to show on a body-coloured dashboard item, given the path lookup for the finish it wants.
function getBodyColourImageFilePath(getFilePathForPaintColourId: (id: string) => string): string {

	// a bespoke body colour is arbitrary, so no swatch file exists for it - and the path lookups answer an unknown id with their
	// fail-safe default, which would show a plausible but wrong colour here. generate a flat swatch of the real colour instead.
	if (Configuration.isBespokePaintColour(Configuration.bodyPaintColour) && Configuration.bespokePaintColour) {
		return Utilities.colourSwatchDataUri(Utilities.rgbaToHex(Configuration.bespokePaintColour, true));
	}

	// otherwise use the selected paint colour's own swatch.
	return `assets/images/configuration-items/paintwork/${getFilePathForPaintColourId(Configuration.bodyPaintColour)}.webp`;
}
